'use client';

import { createContext, useCallback, useContext, useEffect } from 'react';
import { useLocalStorage } from 'usehooks-ts';

const STORAGE_KEY = 'recently-visited';
const MAX_ITEMS = 50;

type EntityType = 'customer';

type VisitedItem = {
  visitedAt: Date;
  entity: EntityType;
  id: string;
};

type RecentlyVisitedStorage = {
  items: VisitedItem[];
};

interface ContextType {
  items: VisitedItem[];
  registerVisit: (entity: EntityType, id: string) => void;
}

const RecentlyVisitedContext = createContext<ContextType | null>(null);

export function useRecentlyVisitedCtx() {
  const ctx = useContext(RecentlyVisitedContext);
  if (!ctx) {
    throw new Error('useRecentlyVisitedCtx must be used within a Provider');
  }
  return ctx;
}

export function useRecentlyVisited() {
  const { items } = useRecentlyVisitedCtx();
  return items;
}

export function useRegisterVisit(entity: EntityType, id: string) {
  const { registerVisit } = useRecentlyVisitedCtx();
  useEffect(() => {
    registerVisit(entity, id);
  }, [entity, id, registerVisit]);
}

interface Props {
  children: React.ReactNode;
}

export function RecentlyVisitedProvider({ children }: Props) {
  const [state, setState] = useLocalStorage<RecentlyVisitedStorage>(STORAGE_KEY, { items: [] });

  const registerVisit = useCallback(
    (entity: EntityType, id: string) => {
      setState((prev) => {
        const items = prev.items.filter((item) => !(item.entity === entity && item.id === id));
        const newItem = { entity, id, visitedAt: new Date() };
        return {
          items: [newItem, ...items].slice(0, MAX_ITEMS),
        };
      });
    },
    [setState]
  );

  return (
    <RecentlyVisitedContext.Provider value={{ items: state.items, registerVisit }}>
      {children}
    </RecentlyVisitedContext.Provider>
  );
}
